import { z } from 'zod';
import { decimalSchema } from './prisma-zod';
import { CustomFieldValueSchema } from './custom-field.types';
import { AllocationStatus } from './member-resource';

export enum ResourceStatus {
  AVAILABLE = 'AVAILABLE',
  ALLOCATED = 'ALLOCATED',
  MAINTENANCE = 'MAINTENANCE',
  INACTIVE = 'INACTIVE',
}

export enum ResourceCategory {
  PERSON = 'PERSON',
  SERVICE = 'SERVICE', 
}

export enum ResourceLifecycleStatus {
  ACTIVE = 'ACTIVE',
  IN_DEVELOPMENT = 'IN_DEVELOPMENT',
  MAINTENANCE = 'MAINTENANCE',
  RETIRED = 'RETIRED',
}

export enum ResourceSortField {
  NAME = 'name',
  CODE = 'code',
  TYPE = 'type',
  STATUS = 'status',
  CREATED_AT = 'createdAt',
  UPDATED_AT = 'updatedAt',
}

// AllocationStatus moved to member-resource.ts (includes PLANNED status)
// BookingStatus kept here for old resource bookings (deprecated)

export enum BookingStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  CANCELLED = 'CANCELLED',
  COMPLETED = 'COMPLETED',
}

export const ResourceTypeSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  code: z.string(),
  description: z.string().optional().nullable(),
  color: z.string().optional().nullable(),
  icon: z.string().optional().nullable(),
  fields: z.any().default([]),
  isActive: z.boolean().default(true),
  metadata: z.any().default({}),
  createdAt: z.date(),
  updatedAt: z.date(),
  _count: z.object({
    resources: z.number().optional(),
  }).optional(),
});

export type ResourceType = z.infer<typeof ResourceTypeSchema>;

export const CreateResourceTypeSchema = ResourceTypeSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  _count: true,
});

export type CreateResourceType = z.infer<typeof CreateResourceTypeSchema>;

export const UpdateResourceTypeSchema = CreateResourceTypeSchema.partial();
export type UpdateResourceType = z.infer<typeof UpdateResourceTypeSchema>;

export const ResourceSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  typeId: z.string().uuid(),
  name: z.string(),
  code: z.string(),
  description: z.string().optional().nullable(),
  status: z.nativeEnum(ResourceStatus).default(ResourceStatus.AVAILABLE),
  metadata: z.any().default({}),
  availability: z.any().default({}),
  costPerHour: decimalSchema.optional().nullable(),
  billPerHour: decimalSchema.optional().nullable(),
  capacity: z.number().int().positive().default(1),
  location: z.string().optional().nullable(),
  tags: z.array(z.string()).default([]),
  
  // Business-specific fields for people and services
  category: z.nativeEnum(ResourceCategory).optional().nullable(),
  utilizationRate: decimalSchema.optional().nullable(),
  billableHoursPerWeek: decimalSchema.optional().nullable(),
  
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().optional(),
  type: ResourceTypeSchema.optional(),
  customFieldValues: z.array(CustomFieldValueSchema).optional(),
  _count: z.object({
    allocations: z.number().optional(),
    bookings: z.number().optional(),
  }).optional(),
});

export type Resource = z.infer<typeof ResourceSchema>;

export const CreateResourceSchema = ResourceSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  type: true,
  customFieldValues: true,
  _count: true,
}).extend({
  customFields: z.record(z.string(), z.any()).optional(),
});

export type CreateResource = z.infer<typeof CreateResourceSchema>;

export const UpdateResourceSchema = CreateResourceSchema.partial();
export type UpdateResource = z.infer<typeof UpdateResourceSchema>;

export const ResourceAllocationSchema = z.object({
  id: z.string().uuid(),
  resourceId: z.string().uuid(),
  projectId: z.string().uuid().optional().nullable(),
  taskId: z.string().uuid().optional().nullable(),
  userId: z.string().uuid().optional().nullable(),
  startDate: z.date(),
  endDate: z.date(),
  allocatedHours: decimalSchema,
  utilizationPercentage: decimalSchema,
  status: z.nativeEnum(AllocationStatus).default(AllocationStatus.CONFIRMED),
  notes: z.string().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  resource: ResourceSchema.optional(),
  project: z.any().optional(),
  task: z.any().optional(),
  user: z.any().optional(),
});

export type ResourceAllocation = z.infer<typeof ResourceAllocationSchema>;

export const CreateResourceAllocationSchema = ResourceAllocationSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  resource: true,
  project: true,
  task: true,
  user: true,
});

export type CreateResourceAllocation = z.infer<typeof CreateResourceAllocationSchema>;

export const UpdateResourceAllocationSchema = CreateResourceAllocationSchema.partial();
export type UpdateResourceAllocation = z.infer<typeof UpdateResourceAllocationSchema>;

export const ResourceBookingSchema = z.object({
  id: z.string().uuid(),
  resourceId: z.string().uuid(),
  userId: z.string().uuid(),
  projectId: z.string().uuid().optional().nullable(),
  taskId: z.string().uuid().optional().nullable(),
  startTime: z.date(),
  endTime: z.date(),
  status: z.nativeEnum(BookingStatus).default(BookingStatus.PENDING),
  purpose: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  isRecurring: z.boolean().default(false),
  recurringPattern: z.any().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  resource: ResourceSchema.optional(),
  user: z.any().optional(),
  project: z.any().optional(),
  task: z.any().optional(),
});

export type ResourceBooking = z.infer<typeof ResourceBookingSchema>;

export const CreateResourceBookingSchema = ResourceBookingSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  resource: true,
  user: true,
  project: true,
  task: true,
});

export type CreateResourceBooking = z.infer<typeof CreateResourceBookingSchema>;

export const UpdateResourceBookingSchema = CreateResourceBookingSchema.partial();
export type UpdateResourceBooking = z.infer<typeof UpdateResourceBookingSchema>;

export const resourceColumns = [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'code', label: 'Code', sortable: true },
  { key: 'type', label: 'Type', sortable: true },
  { key: 'category', label: 'Category', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'capacity', label: 'Capacity', sortable: false },
  { key: 'location', label: 'Location', sortable: false },
  { key: 'billPerHour', label: 'Bill Rate', sortable: false },
  { key: 'costPerHour', label: 'Cost Rate', sortable: false },
  { key: 'utilizationRate', label: 'Utilization', sortable: false },
  { key: 'billableHoursPerWeek', label: 'Billable Hours/Week', sortable: false },
  { key: 'createdAt', label: 'Created', sortable: true },
  { key: 'updatedAt', label: 'Updated', sortable: true },
];

export const resourceTypeColumns = [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'code', label: 'Code', sortable: true },
  { key: 'resources', label: 'Resources', sortable: false },
  { key: 'isActive', label: 'Active', sortable: false },
  { key: 'createdAt', label: 'Created', sortable: true },
  { key: 'updatedAt', label: 'Updated', sortable: true },
];

// Skills
export const SkillSchema = z.object({
  id: z.string(),
  organizationId: z.string().uuid(),
  name: z.string(),
  category: z.string(),
  description: z.string().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  _count: z.object({
    resourceSkills: z.number().optional(),
  }).optional(),
});

export type Skill = z.infer<typeof SkillSchema>;

export const CreateSkillSchema = SkillSchema.omit({
  id: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
  _count: true,
});

export type CreateSkill = z.infer<typeof CreateSkillSchema>;

export const UpdateSkillSchema = CreateSkillSchema.partial();
export type UpdateSkill = z.infer<typeof UpdateSkillSchema>;

export const ResourceSkillSchema = z.object({
  id: z.string(),
  resourceId: z.string().uuid(),
  skillId: z.string(),
  proficiency: z.number().int().min(1).max(5).default(3),
  yearsExperience: z.number().default(0),
  lastUsed: z.date().optional().nullable(),
  certified: z.boolean().default(false),
  certificationDate: z.date().optional().nullable(),
  notes: z.string().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  skill: SkillSchema.optional(),
});

export type ResourceSkill = z.infer<typeof ResourceSkillSchema>;

export const CreateResourceSkillSchema = ResourceSkillSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  skill: true,
});

export type CreateResourceSkill = z.infer<typeof CreateResourceSkillSchema>;

export const UpdateResourceSkillSchema = CreateResourceSkillSchema.partial();
export type UpdateResourceSkill = z.infer<typeof UpdateResourceSkillSchema>;

// Resource Utilization
export const ResourceUtilizationSchema = z.object({
  id: z.string(),
  resourceId: z.string().uuid(),
  date: z.date(),
  totalHours: z.number().default(0),
  billableHours: z.number().default(0),
  utilizationRate: z.number().default(0),
  overtimeHours: z.number().default(0),
  totalCost: z.number().default(0),
  totalRevenue: z.number().default(0),
  metadata: z.any().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  resource: ResourceSchema.optional(),
});

export type ResourceUtilization = z.infer<typeof ResourceUtilizationSchema>;

export const CreateResourceUtilizationSchema = ResourceUtilizationSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  resource: true,
});

export type CreateResourceUtilization = z.infer<typeof CreateResourceUtilizationSchema>;

// Resource Conflicts
export enum ConflictType {
  DOUBLE_BOOKING = 'double-booking',
  OVER_ALLOCATION = 'over-allocation',
  SKILL_MISMATCH = 'skill-mismatch',
}

export enum ConflictSeverity {
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
}

export enum ConflictStatus {
  PENDING = 'pending',
  RESOLVED = 'resolved',
  IGNORED = 'ignored',
}

export const ResourceConflictSchema = z.object({
  id: z.string(),
  resourceId: z.string().uuid(),
  conflictType: z.nativeEnum(ConflictType),
  severity: z.nativeEnum(ConflictSeverity),
  startDate: z.date(),
  endDate: z.date(),
  conflictingItems: z.any(),
  status: z.nativeEnum(ConflictStatus).default(ConflictStatus.PENDING),
  resolution: z.any().optional().nullable(),
  resolvedBy: z.string().uuid().optional().nullable(),
  resolvedAt: z.date().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  resource: ResourceSchema.optional(),
  resolver: z.any().optional(),
});

export type ResourceConflict = z.infer<typeof ResourceConflictSchema>;

// Resource Predictions
export const ResourcePredictionSchema = z.object({
  id: z.string(),
  resourceId: z.string().uuid(),
  predictionDate: z.date(),
  predictedUtilization: z.number(),
  confidenceScore: z.number(),
  demandForecast: z.any().optional().nullable(),
  riskFactors: z.any().optional().nullable(),
  recommendations: z.any().optional().nullable(),
  modelVersion: z.string(),
  createdAt: z.date(),
  resource: ResourceSchema.optional(),
});

export type ResourcePrediction = z.infer<typeof ResourcePredictionSchema>;